import React, { useState, useRef } from 'react';

/**
 * Photos attached to a single line item, shared by the FF&E and Checklist
 * sheets so both behave identically.
 *
 * props:
 *   item      - the line item ({ id, name, photos })
 *   onClose   - close the modal
 *   onSaved   - called with the new photos array after any change
 */
const ItemPhotosModal = ({ item, onClose, onSaved, onMainChanged }) => {
  const [photos, setPhotos] = useState(
    (item?.photos || []).filter((p) => typeof p === 'string')
  );
  const [mainUrl, setMainUrl] = useState(item?.image_url || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const backendUrl = () => (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

  const commit = (next) => {
    setPhotos(next);
    if (onSaved) onSaved(next);
  };

  // Make this photo the item's MAIN image (the one sheets show). Writes to the
  // real item so it syncs everywhere, then tells the parent to refresh.
  const setAsMain = async (src) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl()}/api/items/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: src }),
      });
      if (!res.ok) throw new Error(`Could not set main photo (${res.status})`);
      setMainUrl(src);
      if (onMainChanged) onMainChanged(src);
    } catch (err) {
      setError(err.message || 'Could not set main photo');
    } finally {
      setBusy(false);
    }
  };

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));

      const res = await fetch(`${backendUrl()}/api/items/${item.id}/photos`, {
        method: 'POST',
        body: form,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Upload failed (${res.status})`);
      commit(body.photos || []);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (index) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl()}/api/items/${item.id}/photos/${index}`, {
        method: 'DELETE',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Delete failed (${res.status})`);
      commit(body.photos || []);
    } catch (err) {
      console.error('Photo delete failed:', err);
      setError(err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="item-photos-modal"
    >
      <div
        data-testid="item-photos-panel"
        className="rounded-2xl border-2 border-[#D4A574] max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6"
        style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.98) 0%, rgba(30,30,30,0.95) 50%, rgba(0,0,0,0.98) 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-[#D4A574]">Photos</h3>
            <p className="text-sm text-[#D4C5A9] mt-1">{item?.name || 'Line item'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#D4A574] hover:text-red-400 text-2xl leading-none"
            data-testid="item-photos-close"
          >
            &times;
          </button>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFiles}
          data-testid="item-photos-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid="item-photos-add"
          className="w-full mb-4 bg-[#D4A574] hover:bg-[#B49B7E] disabled:opacity-50 text-black px-4 py-3 rounded-lg font-bold"
        >
          {busy ? 'Working…' : '+ Add Photos'}
        </button>

        {error && (
          <p className="text-red-400 text-sm mb-3" data-testid="item-photos-error">{error}</p>
        )}

        {photos.length === 0 ? (
          <p className="text-[#B49B7E] text-sm text-center py-8" data-testid="item-photos-empty">
            No photos on this item yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="item-photos-grid">
            {(photos || []).map((src, i) => {
              const isMain = !!mainUrl && src === mainUrl;
              return (
              <div key={`${i}-${src.slice(-24)}`} className="relative group">
                <img
                  data-testid={`item-photo-${i}`}
                  src={src}
                  alt={`${item?.name || 'Item'} photo ${i + 1}`}
                  onClick={() => setPreview(src)}
                  className={`w-full h-32 object-cover rounded-lg border cursor-zoom-in ${isMain ? 'border-2 border-[#D4A574]' : 'border-[#D4A574]/40'}`}
                />
                {isMain && (
                  <span className="absolute bottom-1 left-1 bg-[#D4A574] text-black text-[9px] font-bold rounded px-1.5 py-0.5">MAIN</span>
                )}
                {!isMain && (
                  <button
                    onClick={() => setAsMain(src)}
                    disabled={busy}
                    title="Use as the main photo (shows on all sheets)"
                    data-testid={`item-photo-setmain-${i}`}
                    className="absolute bottom-1 left-1 bg-black/80 text-[#D4A574] hover:text-[#E2B989] rounded px-1.5 py-0.5 text-[9px] font-bold border border-[#D4A574]/50"
                  >
                    ★ Set main
                  </button>
                )}
                <button
                  onClick={() => removePhoto(i)}
                  disabled={busy}
                  title="Remove photo"
                  data-testid={`item-photo-remove-${i}`}
                  className="absolute top-1 right-1 bg-black/80 text-red-400 hover:text-red-300 rounded-full w-7 h-7 text-sm font-bold border border-red-400/50"
                >
                  &times;
                </button>
              </div>
              );
            })}
          </div>
        )}

        <p className="text-[#B49B7E] text-xs mt-4">
          {photos.length} photo{photos.length === 1 ? '' : 's'} · up to 12 per item
        </p>
      </div>

      {preview && (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[70] p-6"
          onClick={(e) => { e.stopPropagation(); setPreview(null); }}
          data-testid="item-photo-preview"
        >
          <img src={preview} alt="Full size" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default ItemPhotosModal;
